import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import '../../../stylesheets/PatientList.css';

const InventoryList = ({ inventories = [], onEdit , onDelete}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const itemsPerPage = 10;

  // 🔍 Filtered + Sorted
  const filteredInventories = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const filtered = inventories.filter((item) => item.name.toLowerCase().includes(term)
      || item.medication_code.toLowerCase().includes(term));

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        if (typeof aVal === 'string') {
          return sortConfig.direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      });
    }

    return filtered;
  }, [inventories, searchTerm, sortConfig]);

  const totalPages = Math.ceil(filteredInventories.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredInventories.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key && prev.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const getArrow = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const handlePrevious = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNext = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));

  return (
    <div className="flex flex-col min-h-screen mt-4 overflow-hidden">
      {/* 🔍 Search Bar */}
      <div className="relative mb-4  search">
        <input
          type="text"
          placeholder="Search by name or code..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className="px-4 py-2 pr-10 border border-gray-300 rounded w-full"
        />
        {searchTerm && (
          <button
            onClick={() => {
              setSearchTerm('');
              setCurrentPage(1);
            }}
            type="button"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 text-lg font-bold focus:outline-none"
          >
            &times;
          </button>
        )}
      </div>

      {/* 📋 Table */}
      <table className=" list-table min-w-full border-collapse border border-gray-200 bg-white shadow rounded">
        <thead className="sticky top-0 bg-gray-100 text-gray-700 z-10">
          <tr>
            <th className="px-4 py-2 border cursor-pointer" onClick={() => handleSort('medication_code')}>
              Code
              {getArrow('medication_code')}
            </th>
            <th className="px-4 py-2 border cursor-pointer" onClick={() => handleSort('name')}>
              Name
              {getArrow('name')}
            </th>
            <th className="px-4 py-2 border cursor-pointer" onClick={() => handleSort('batch_number')}>
              Batch
              {getArrow('batch_number')}
            </th>
            <th className="px-4 py-2 border cursor-pointer" onClick={() => handleSort('quantity')}>
              Qty
              {getArrow('quantity')}
            </th>
            <th className="px-4 py-2 border cursor-pointer" onClick={() => handleSort('expiry_date')}>
              Expiry
              {getArrow('expiry_date')}
            </th>
            <th className="px-4 py-2 border cursor-pointer" onClick={() => handleSort('reorder_level')}>
              Reorder
              {getArrow('reorder_level')}
            </th>
            <th className="px-4 py-2 border">Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.length > 0 ? (
            currentItems.map((item) => (
              <tr
                key={item.id}
                className={`border-t hover:bg-gray-50 ${
                  new Date(item.expiry_date) < new Date()
                    ? 'bg-red-100'
                    : item.quantity < item.reorder_level
                      ? 'bg-yellow-100'
                      : ''
                }`}
              >

                <td className="px-4 py-2 border text-center">{item.medication_code}</td>
                <td className="px-4 py-2 border text-center">{item.name}</td>
                <td className="px-4 py-2 border text-center">{item.batch_number}</td>
                <td className="px-4 py-2 border text-center">
                  {item.quantity}
                  {' '}
                  {' '}
                  {' '}
                  {item.quantity < item.reorder_level && (
                  <span className="ml-2 text-yellow-600 font-semibold">⚠️</span>
                  )}
                </td>
                <td className="px-4 py-2 border text-center">
                  {item.expiry_date}
                  {' '}
                  {' '}
                  {' '}
                  {new Date(item.expiry_date) < new Date() && (
                  <span className="ml-2 text-red-600 font-semibold">⛔</span>
                  )}
                </td>
                <td className="px-4 py-2 border text-center">{item.reorder_level}</td>
                <td className="px-4 py-2 border text-center">
                  <div className='action-btns'>
                    <button
                      onClick={() => onEdit(item)}
                      type='button'
                      className="primary"
                    >
                      Edit
                    </button>

                    <button
                      onClick={() => onDelete(item.id, item.name, item.medication_code, item.batch_number)}
                      type='button'
                      className="danger"
                    >
                      Delete
                    </button> 
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7" className="text-center py-4 text-gray-600">
                No matching inventory found.
              </td>
            </tr>
          )}

        </tbody>
      </table>

      {/* 🔁 Pagination Controls */}
      <div className="mt-auto flex justify-between items-center py-4">
        <button
          onClick={handlePrevious}
          type='button'
          disabled={currentPage === 1}
          className="px-4 py-2 bg-gray-200 text-sm rounded disabled:opacity-50"
        >
          Previous
        </button>
        <span className="text-sm text-gray-700">
          Page
          {' '}
          {currentPage}
          {' '}
          of
          {' '}
          {totalPages}
        </span>
        <button
          onClick={handleNext}
          disabled={currentPage === totalPages}
          type='button'
          className="px-4 py-2 bg-gray-200 text-sm rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

InventoryList.propTypes = {
  inventories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      medication_code: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      batch_number: PropTypes.string,
      quantity: PropTypes.number.isRequired,
      expiry_date: PropTypes.string.isRequired,
      reorder_level: PropTypes.number.isRequired,
    })
  ).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};


export default InventoryList;
